// What a milestone gave, shown as it lands in the celebration: the reward
// flips in, a burst of light and a ring of sparks mark the landing.
// Later: other reward types (e.g. access to a game) show what they unlock,
// such as a game cover.

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ASSETS } from '@airhead-games/shared/configs';
import type { AchievementReward } from '@airhead-games/shared/types';
import { rewardLabel } from './AchievementChips';

// The burst waits for the reward to land, not for it to start.
const LANDING_S = 0.25;

// Sparks thrown out as the reward lands: evenly spread, alternating reach.
const SPARKS = Array.from({ length: 10 }, (_, index) => {
  const angle = (index / 10) * Math.PI * 2;
  const reach = index % 2 === 0 ? 120 : 90;
  return { x: Math.cos(angle) * reach, y: Math.sin(angle) * reach };
});

// A bigger pile for a bigger reward.
const loonImage = (amount: number): string => {
  if (amount >= 100) return ASSETS.loonSimple100;
  if (amount >= 50) return ASSETS.loonSimple050;
  if (amount >= 25) return ASSETS.loonSimple025;
  if (amount >= 2) return ASSETS.loonSimple010;
  return ASSETS.loonSimple001;
};

const RewardImage: React.FC<{ reward: AchievementReward }> = ({ reward }) => (
  <img
    src={loonImage(reward.amount)}
    alt={rewardLabel(reward)}
    className="size-44 object-contain drop-shadow-lg"
  />
);

type RewardRevealProps = {
  reward: AchievementReward;
  /** Seconds before the reward starts to flip in. */
  delay: number;
};

/** Needs a perspective on an ancestor for its flip. */
const AchievementRewardReveal: React.FC<RewardRevealProps> = ({
  reward,
  delay,
}) => {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, delay }}
      >
        <RewardImage reward={reward} />
      </motion.div>
    );
  }

  const landing = delay + LANDING_S;
  return (
    <div
      className="relative grid place-items-center"
      style={{ transformStyle: 'preserve-3d' }}
    >
      <motion.div
        aria-hidden="true"
        className="absolute size-48 rounded-full bg-[radial-gradient(circle,var(--color-yellow-200),transparent_65%)]"
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: [0.7, 1.7], opacity: [0, 0.8, 0] }}
        transition={{
          duration: 1,
          delay: landing,
          ease: 'easeOut',
          opacity: { duration: 1, delay: landing, times: [0, 0.2, 1] },
        }}
      />
      {SPARKS.map((spark, index) => (
        <motion.span
          key={index}
          aria-hidden="true"
          className="absolute size-2 rounded-full bg-yellow-300"
          initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
          animate={{
            x: spark.x,
            y: spark.y,
            scale: [0, 1.4, 0],
            opacity: [1, 1, 0],
          }}
          transition={{ duration: 0.8, delay: landing, ease: 'easeOut' }}
        />
      ))}
      <motion.div
        initial={{ rotateY: -200, scale: 0.3, opacity: 0 }}
        animate={{ rotateY: 0, scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 170, damping: 13, delay }}
      >
        <RewardImage reward={reward} />
      </motion.div>
    </div>
  );
};

export default AchievementRewardReveal;
